import { useQuery } from '@tanstack/react-query';
import React, {
  createContext,
  useContext,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { apiClient } from '@/api/client';
import {
  API_USER,
  API_USER_PROFILES_BY_YEAR,
  API_USER_PROFILES_SYNC,
} from '@/api/endpoints';
import { USER_PROFILES_QUERY_KEY, USER_QUERY_KEY } from '@/api/queryKeys';
import { getProfileIdentity, subscribeProfileIdentity } from '@/auth/session';
import type {
  ProfileEntryResponse,
  StudyYearResponse,
  UserInfoResponse,
  UserProfilesResponse,
} from '@/types/user';
import { unwrapAxiosResponse } from '@/utils/axios';
import { ErrorMessage, logRequestError } from '@/utils/errors';
import {
  getNameWithRole,
  getStudyYearProfiles,
  isSameProfile,
} from '@/utils/person';

interface UserContextValue {
  user: UserInfoResponse | undefined;
  userDisplayName: string;
  profiles: UserProfilesResponse | undefined;
  currentProfile: ProfileEntryResponse | undefined;
  currentStudyYear: StudyYearResponse | undefined;
  isUserFetching: boolean;
  isProfilesFetching: boolean;
  isFetching: boolean;
}

const UserContext = createContext<UserContextValue | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<ProviderProps> = ({ children }) => {
  const { data: user, isFetching: isUserFetching } = useQuery<UserInfoResponse>(
    {
      queryKey: USER_QUERY_KEY,
      queryFn: () =>
        apiClient.get<UserInfoResponse>(API_USER).then(unwrapAxiosResponse),
      staleTime: Infinity,
    },
  );

  const { data: profiles, isFetching: isProfilesFetching } =
    useQuery<UserProfilesResponse>({
      queryKey: USER_PROFILES_QUERY_KEY,
      queryFn: async () => {
        await apiClient
          .post(API_USER_PROFILES_SYNC)
          .catch((error) => logRequestError(ErrorMessage.SyncProfiles, error));

        return apiClient
          .get<UserProfilesResponse>(API_USER_PROFILES_BY_YEAR)
          .then(unwrapAxiosResponse);
      },
      staleTime: Infinity,
    });

  const profileIdentity = useSyncExternalStore(
    subscribeProfileIdentity,
    getProfileIdentity,
  );

  const currentStudyYear = profileIdentity
    ? profiles?.studyYears.find((studyYear) =>
        getStudyYearProfiles(studyYear).some((profile) =>
          isSameProfile(profile.identity, profileIdentity),
        ),
      )
    : undefined;

  const currentProfile =
    profileIdentity && currentStudyYear
      ? getStudyYearProfiles(currentStudyYear).find((profile) =>
          isSameProfile(profile.identity, profileIdentity),
        )
      : undefined;

  const userDisplayName = getNameWithRole({
    person: user,
    personType: user?.personType,
  });

  return (
    <UserContext.Provider
      value={{
        user,
        userDisplayName,
        profiles,
        currentProfile,
        currentStudyYear,
        isUserFetching,
        isProfilesFetching,
        isFetching: isUserFetching || isProfilesFetching,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = (): UserContextValue => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('missing userProvider');
  }

  return context;
};
